// Playground-only HTTP admin-generation factory.
//
// This lives apart from `adminGen.ts` because `authedFetch` reaches
// `firebaseClient`, whose dynamic `import('firebase/*')` calls Vite resolves
// (and cannot tree-shake away) in any consumer that can reach this module. The
// production barrels must not reach it: a creator game that does not enable
// `rundotGamePlaygroundPlugin()` never installs Firebase, and resolving those
// imports would fail its `vite build`. Only HttpAppApi — itself Playground-only
// — may import from here.
import { authedFetch } from './httpUtils'
import type {
  AdminGenApi,
  AdminGenBrowseParams,
  AdminGenBrowseResponse,
  AdminGenListReportsParams,
  AdminGenListReportsResponse,
  AdminGenResolveAction,
} from './adminGen'

export function createHttpAdminGenApi<TEntry, TReport>(
  routePrefix: string,
  appId: string,
): AdminGenApi<TEntry, TReport> {
  return {
    async browse(params?: AdminGenBrowseParams): Promise<AdminGenBrowseResponse<TEntry>> {
      const response = await authedFetch(
        `/v1/${routePrefix}/${appId}/admin/browse`,
        { method: 'POST', body: JSON.stringify(params ?? {}) },
      )
      return (await response.json()) as AdminGenBrowseResponse<TEntry>
    },

    async removeEntry(generationId: string): Promise<void> {
      await authedFetch(
        `/v1/${routePrefix}/${appId}/admin/generations/${generationId}/remove`,
        { method: 'POST' },
      )
    },

    async listReports(params?: AdminGenListReportsParams): Promise<AdminGenListReportsResponse<TReport>> {
      const queryParams = new URLSearchParams()
      if (params?.status) queryParams.set('status', params.status)
      if (params?.cursor) queryParams.set('cursor', params.cursor)
      if (params?.limit) queryParams.set('limit', String(params.limit))
      const qs = queryParams.toString()
      const path = `/v1/${routePrefix}/${appId}/admin/reports${qs ? `?${qs}` : ''}`
      const response = await authedFetch(path, { method: 'GET' })
      return (await response.json()) as AdminGenListReportsResponse<TReport>
    },

    async resolveReport(reportId: string, action: AdminGenResolveAction): Promise<void> {
      await authedFetch(
        `/v1/${routePrefix}/${appId}/admin/reports/${reportId}/resolve`,
        { method: 'POST', body: JSON.stringify({ action }) },
      )
    },
  }
}
